import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, Check, ExternalLink, Filter } from "lucide-react";
import Navbar from "@/components/Navbar";
import {
  getRobinhoodOverview,
  getRobinhoodAssets,
  getRobinhoodLeaderboard,
  getRobinhoodHype,
  getRobinhoodCallers,
  getRobinhoodMetrics,
  shortContract,
  shortWallet,
  formatRobinhoodApiError,
  ROBINHOOD_EMPTY,
  ROBINHOOD_PRIMARY_RANK_LABEL,
  ROBINHOOD_STAGING_DISCLOSURE,
  isSourceBackedStockToken,
} from "@/lib/robinhoodApi";
import { fmtUSD } from "@/lib/format";
import { switchEvmChain, identifyRobinhoodWallet } from "@/lib/onchain";
import { toast } from "sonner";
import ClassificationBadges from "@/components/ClassificationBadges";
import { TAXONOMY_FILTERS } from "@/lib/classification";

const ASSET_FILTERS = [
  { key: "", label: "All" },
  { key: "crypto_token", label: "Crypto" },
  { key: "meme_token", label: "Meme" },
  { key: "rwa", label: "RWA" },
  { key: "tokenized_equity", label: "Stock Tokens" },
  { key: "tokenized_etf", label: "Stock Token ETF" },
  { key: "stablecoin", label: "Stable" },
  { key: "unknown", label: "Unknown" },
];

const MARKET_SORTS = [
  { key: "newest", label: "Newest" },
  { key: "volume", label: "24h volume" },
  { key: "liquidity", label: "Liquidity" },
  { key: "momentum", label: "Momentum" },
];

function CopyAddr({ address, testId = "rh-copy-address" }) {
  const [ok, setOk] = useState(false);
  if (!address) return null;
  return (
    <button
      type="button"
      data-testid={testId}
      title={address}
      aria-label={`Copy address ${address}`}
      className="inline-flex max-w-full items-center gap-1 font-mono text-[11px] text-zinc-400 hover:text-white"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(address);
        setOk(true);
        setTimeout(() => setOk(false), 1200);
      }}
    >
      <span className="truncate">{shortContract(address)}</span>
      {ok ? <Check className="h-3 w-3 shrink-0" /> : <Copy className="h-3 w-3 shrink-0" />}
    </button>
  );
}

function Stat({ label, value, testId }) {
  if (value == null || value === "") return null;
  return (
    <div data-testid={testId} className="min-w-[7rem]">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold tabular-nums text-white">{value}</div>
    </div>
  );
}

function AssetRow({ a, mode }) {
  const href = a.slug ? `/token/${a.slug}` : null;
  const inner = (
    <>
      <div className="flex min-w-0 items-center gap-3">
        {a.logo ? (
          <img
            src={a.logo}
            alt=""
            className="h-8 w-8 shrink-0 rounded-full bg-white/5 object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 font-mono text-[10px] text-zinc-500">
            {(String(a?.ticker || a?.symbol || "?")).slice(0, 3)}
          </div>
        )}
        <div className="min-w-0">
          <div className="truncate font-display font-bold">{a.ticker || a.symbol || "—"}</div>
          <div className="truncate text-xs text-zinc-500">{a.name || "Unnamed"}</div>
          <ClassificationBadges entity={a} testId={`rh-badges-${a.contract_address || a.slug || "row"}`} />
        </div>
      </div>
      <div className="hidden min-w-0 flex-col items-end sm:flex">
        <div className="text-[10px] uppercase tracking-wider text-zinc-600">Robinhood · 4663</div>
        <CopyAddr address={a.contract_address} />
      </div>
      {mode === "backing" ? (
        <div className="shrink-0 text-right font-mono text-sm text-emerald-300">
          {fmtUSD(a.backing_total_usd ?? a.total_backed_usd)}
          <div className="text-[10px] uppercase tracking-wider text-zinc-600">
            Rank {a.filter_rank || a.backing_rank || a.rank || "—"}
          </div>
        </div>
      ) : mode === "hype" ? (
        <div className="shrink-0 text-right font-mono text-sm text-amber-200">
          {a.hype_score ?? a.score ?? "—"}
          <div className="text-[10px] uppercase tracking-wider text-zinc-600">Hype</div>
        </div>
      ) : (
        <div className="shrink-0 text-right font-mono text-sm text-zinc-300">
          {a.market_volume_24h_usd != null ? fmtUSD(a.market_volume_24h_usd) : "—"}
          <div className="text-[10px] uppercase tracking-wider text-zinc-600">
            {a.market_stale ? "Stale vol" : "Ext. 24h vol"}
          </div>
        </div>
      )}
    </>
  );
  const cls =
    "grid min-w-0 grid-cols-[1fr_auto] items-center gap-3 border-b border-white/[0.06] px-3 py-3 transition-colors hover:bg-white/[0.03] sm:grid-cols-[1.2fr_1fr_auto]";
  if (href) {
    return (
      <Link to={href} data-testid={`rh-asset-${a.contract_address || a.slug}`} className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={`rh-asset-${a.contract_address || a.slug}`} className={cls}>
      {inner}
    </div>
  );
}

export default function RobinhoodEcosystemPage() {
  const [overview, setOverview] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [board, setBoard] = useState([]);
  const [assets, setAssets] = useState([]);
  const [hype, setHype] = useState([]);
  const [callers, setCallers] = useState([]);
  const [stockTokenRows, setStockTokenRows] = useState([]);
  const [assetType, setAssetType] = useState("");
  const [taxonomyKey, setTaxonomyKey] = useState("");
  const [marketSort, setMarketSort] = useState("newest");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rhWallet, setRhWallet] = useState({ available: false });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, met, lb, as, hy, ca, stocks] = await Promise.all([
        getRobinhoodOverview(),
        getRobinhoodMetrics().catch(() => null),
        getRobinhoodLeaderboard({ limit: 25 }),
        getRobinhoodAssets({
          limit: 40,
          sort: marketSort,
          ...(assetType ? { asset_type: assetType } : {}),
          ...(TAXONOMY_FILTERS.find((f) => f.key === taxonomyKey)?.params || {}),
        }),
        getRobinhoodHype({ limit: 25 }),
        getRobinhoodCallers({ limit: 25 }),
        getRobinhoodAssets({
          limit: 40,
          sort: "volume",
          asset_type: "tokenized_equity",
        }).catch(() => null),
      ]);
      setOverview(ov);
      setMetrics(met);
      setBoard(lb?.entities || []);
      setAssets(as?.assets || []);
      setHype((hy?.tokens || []).map((t) => ({
        ...(t.entity || {
          id: t.contract_address,
          ticker: t.symbol,
          name: t.name,
          contract_address: t.contract_address,
          chain: t.chain,
          backing_total_usd: t.backing_total_usd,
          logo: t.logo,
        }),
        logo: t.logo || t.entity?.logo,
        hype_score: t.hype_score,
        components: t.components,
      })));
      setCallers(ca?.callers || []);
      setStockTokenRows((stocks?.assets || []).filter(isSourceBackedStockToken));
    } catch (e) {
      setError(formatRobinhoodApiError(e));
    } finally {
      setLoading(false);
    }
  }, [assetType, marketSort, taxonomyKey]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setRhWallet(identifyRobinhoodWallet());
  }, []);

  const stockTokens = useMemo(() => {
    if (stockTokenRows.length) return stockTokenRows;
    return assets.filter(isSourceBackedStockToken);
  }, [stockTokenRows, assets]);

  const onSwitchNetwork = async () => {
    try {
      await switchEvmChain("robinhood");
      toast.success("Switched to Robinhood Chain (4663)");
    } catch (e) {
      toast.error(e?.message?.slice(0, 100) || "Network switch failed");
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#050505] text-white" data-testid="rh-ecosystem-page">
      <Navbar category="tokens" />

      <section className="relative overflow-hidden border-b border-white/10">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 15% 20%, rgba(16,185,129,0.18), transparent 55%), "
              + "radial-gradient(ellipse 50% 40% at 85% 10%, rgba(251,191,36,0.08), transparent 50%), "
              + "linear-gradient(180deg, #0a1210 0%, #050505 70%)",
          }}
        />
        <div className="relative mx-auto max-w-6xl px-4 pb-12 pt-10 sm:px-6 sm:pt-14">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="font-display text-sm font-bold uppercase tracking-[0.28em] text-emerald-400/90"
          >
            BackOnFlip
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.04 }}
            className="mt-3 text-xs uppercase tracking-[0.22em] text-zinc-500"
            data-testid="rh-hero-network"
          >
            Robinhood Chain · Chain ID 4663 · EVM · ETH gas
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="mt-3 max-w-3xl font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-6xl"
            data-testid="rh-hero-title"
          >
            Robinhood
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.12 }}
            className="mt-4 max-w-xl text-base text-zinc-400 sm:text-lg"
            data-testid="rh-hero-sub"
          >
            Discover what capital backs. See who called it first. Track who keeps being right.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-7 flex flex-wrap items-center gap-3"
          >
            <button
              type="button"
              data-testid="rh-switch-network"
              onClick={onSwitchNetwork}
              className="rounded-full bg-emerald-400 px-5 py-2.5 text-sm font-bold text-black transition hover:bg-emerald-300"
            >
              Use Robinhood Chain (4663)
            </button>
            <a
              href="https://robinhoodchain.blockscout.com"
              target="_blank"
              rel="noreferrer"
              className="rounded-full border border-white/20 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-white/5"
            >
              Explorer
            </a>
            <Link
              to="/?chain=robinhood"
              className="rounded-full border border-white/20 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-white/5"
            >
              Board filter
            </Link>
            {rhWallet.available && (
              <span className="text-xs text-emerald-300/80" data-testid="rh-wallet-detected">
                Robinhood Wallet detected
              </span>
            )}
          </motion.div>
          <p
            className="mt-5 max-w-2xl text-[11px] leading-relaxed text-zinc-600"
            data-testid="rh-staging-disclosure"
          >
            {ROBINHOOD_STAGING_DISCLOSURE}
          </p>
          <div className="mt-10 flex flex-wrap gap-8 border-t border-white/10 pt-6">
            <Stat
              label="Discovered assets"
              value={overview?.stats?.discovered_assets ?? metrics?.assets_discovered}
              testId="rh-stat-discovered"
            />
            <Stat
              label="BackOnFlip ranked"
              value={overview?.stats?.backonflip_ranked_assets ?? metrics?.backonflip_ranked_assets}
              testId="rh-stat-ranked"
            />
            <Stat
              label="Stock Tokens"
              value={overview?.stats?.source_backed_stock_tokens ?? metrics?.source_backed_stock_tokens}
              testId="rh-stat-stock-tokens"
            />
            <Stat
              label="Active callers"
              value={metrics?.robinhood_caller_count ?? callers.length}
              testId="rh-stat-callers"
            />
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-6xl space-y-14 px-4 py-10 sm:px-6">
        {error && (
          <div data-testid="rh-error" className="border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}
        {loading && (
          <div data-testid="rh-loading" className="text-sm text-zinc-500">Loading Robinhood ecosystem…</div>
        )}

        <div className="grid gap-3 sm:grid-cols-2" data-testid="rh-rank-vs-market">
          <div className="border-l-2 border-emerald-400 pl-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">BackOnFlip Rank</div>
            <p className="mt-1 text-sm text-zinc-400">
              {ROBINHOOD_PRIMARY_RANK_LABEL}. External volume cannot change this order.
            </p>
          </div>
          <div className="border-l-2 border-amber-300/80 pl-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-200">Market Activity</div>
            <p className="mt-1 text-sm text-zinc-400">
              External on-chain market data for discovery and Hype Radar.
            </p>
          </div>
        </div>

        <section data-testid="rh-section-leaderboard">
          <h2 className="font-display text-2xl font-bold">BackOnFlip Leaderboard</h2>
          <p className="mt-1 text-sm text-zinc-500" data-testid="rh-rank-label">
            {ROBINHOOD_PRIMARY_RANK_LABEL} · (−total_backed_usd, entity_id)
          </p>
          <div className="mt-4 border-t border-white/10">
            {!loading && board.length === 0 && (
              <p className="py-8 text-sm text-zinc-500" data-testid="rh-board-empty">
                {ROBINHOOD_EMPTY.backing}
              </p>
            )}
            {board.map((a) => (
              <AssetRow key={a.id || a.contract_address} a={a} mode="backing" />
            ))}
          </div>
        </section>

        <section data-testid="rh-section-hype">
          <h2 className="font-display text-2xl font-bold">Hype Radar</h2>
          <p className="mt-1 text-sm text-zinc-500" data-testid="rh-hype-disclaimer">
            Hype Radar detects accelerating activity; it does not predict returns.
            Distinct from verified Backing rank.
          </p>
          <div className="mt-4 border-t border-white/10">
            {!loading && hype.length === 0 && (
              <p className="py-8 text-sm text-zinc-500" data-testid="rh-hype-empty">No hype signals yet.</p>
            )}
            {hype.map((a) => (
              <AssetRow key={`h-${a.contract_address || a.id}`} a={a} mode="hype" />
            ))}
          </div>
        </section>

        <section data-testid="rh-section-assets">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-display text-2xl font-bold">New Assets</h2>
              <p className="mt-1 text-sm text-zinc-500">Market Activity · discovery metadata only</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="h-3.5 w-3.5 text-zinc-500" />
              {ASSET_FILTERS.map((f) => (
                <button
                  key={f.key || "all"}
                  type="button"
                  data-testid={`rh-filter-${f.key || "all"}`}
                  onClick={() => { setAssetType(f.key); setTaxonomyKey(""); }}
                  className={`px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${
                    assetType === f.key && !taxonomyKey ? "text-emerald-300" : "text-zinc-500 hover:text-white"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="rh-taxonomy-filters">
            {TAXONOMY_FILTERS.filter((f) => f.key).map((f) => (
              <button
                key={f.key}
                type="button"
                data-testid={`rh-taxonomy-${f.key.replace(":", "-")}`}
                onClick={() => { setTaxonomyKey(f.key); setAssetType(""); }}
                className={`border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                  taxonomyKey === f.key
                    ? "border-emerald-300/40 text-emerald-200"
                    : "border-white/10 text-zinc-500 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {MARKET_SORTS.map((s) => (
              <button
                key={s.key}
                type="button"
                data-testid={`rh-sort-${s.key}`}
                onClick={() => setMarketSort(s.key)}
                className={`border px-3 py-1 text-xs ${
                  marketSort === s.key
                    ? "border-amber-300/40 text-amber-100"
                    : "border-white/10 text-zinc-500"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="mt-4 border-t border-white/10">
            {!loading && assets.length === 0 && (
              <p className="py-8 text-sm text-zinc-500" data-testid="rh-assets-empty">
                No discovered Robinhood assets for this filter yet.
              </p>
            )}
            {assets.map((a) => (
              <AssetRow key={`m-${a.contract_address || a.id}`} a={a} mode="market" />
            ))}
          </div>
        </section>

        <section data-testid="rh-section-rwa">
          <h2 className="font-display text-2xl font-bold">Robinhood Stock Tokens</h2>
          <p className="mt-1 text-sm text-zinc-500">
            Source-backed via official Robinhood RHJ catalog. Underlying reference ≠ token market ≠ BackOnFlip Backing.
          </p>
          <div className="mt-4 border-t border-white/10">
            {stockTokens.length === 0 && (
              <p className="py-8 text-sm text-zinc-500" data-testid="rh-rwa-empty">
                No source-backed Stock Tokens in the current result set yet.
              </p>
            )}
            {stockTokens.map((a) => (
              <div
                key={`rwa-${a.contract_address}`}
                className="min-w-0 border-b border-white/[0.06]"
                data-testid={`rh-stock-token-${a.contract_address}`}
              >
                <AssetRow a={a} mode="market" />
                {a.underlying_asset && (
                  <div className="mt-2 px-3 pb-3 pl-11 text-xs text-zinc-500">
                    Underlying: {a.underlying_asset.underlying_ticker || a.underlying_asset.underlying_name || "—"}
                    {a.underlying_asset.issuer ? ` · Issuer ${a.underlying_asset.issuer}` : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section data-testid="rh-section-callers">
          <h2 className="font-display text-2xl font-bold">Top Callers</h2>
          <p className="mt-1 text-sm text-zinc-500">
            Who called Robinhood assets early — outcomes stay pending until resolved.
          </p>
          <div className="mt-4 border-t border-white/10">
            {callers.length === 0 && (
              <p className="py-8 text-sm text-zinc-500" data-testid="rh-callers-empty">
                {ROBINHOOD_EMPTY.callers}
              </p>
            )}
            {callers.map((c) => {
              const wallet = c.wallet || c.caller_id;
              const rh = c.robinhood || {};
              const display = c.handle || c.slug || shortWallet(wallet);
              return (
                <div
                  key={c.caller_id}
                  data-testid={`rh-caller-${c.caller_id}`}
                  className="flex min-w-0 flex-wrap items-center justify-between gap-3 border-b border-white/[0.06] px-3 py-3"
                >
                  <div className="min-w-0">
                    <div
                      className="truncate font-display font-bold"
                      title={wallet}
                      aria-label={wallet ? `Caller wallet ${wallet}` : undefined}
                    >
                      {display?.startsWith?.("0x") ? shortWallet(display) : display}
                    </div>
                    <div className="mt-0.5 text-xs text-zinc-500">
                      {rh.first_call_at
                        ? `Earliest call ${String(rh.first_call_at).slice(0, 10)}`
                        : ROBINHOOD_EMPTY.outcomes}
                    </div>
                  </div>
                  <div className="shrink-0 font-mono text-xs text-zinc-400">
                    {rh.assets_called ?? rh.entities?.length ?? 0} assets · {rh.calls ?? 0} calls ·{" "}
                    {rh.early_calls ?? 0} early
                    {rh.open != null ? ` · ${rh.open} open` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section data-testid="rh-pitch" className="border-t border-white/10 pt-10 pb-16">
          <h2 className="font-display text-3xl font-black tracking-tight">
            BackOnFlip for Robinhood Chain
          </h2>
          <p className="mt-3 max-w-2xl text-zinc-400">
            Economic leaderboard, caller reputation, Stock Token discovery, and Hype Radar —
            built so the Robinhood ecosystem can evaluate BackOnFlip as infrastructure.
            No partnership, endorsement, listing, or $FLIP mint is implied.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-zinc-500">
            <li>· Primary rank = verified Backing only</li>
            <li>· Market data powers discovery — never rank</li>
            <li>· No token-sale language · no partnership claimed</li>
          </ul>
          <a
            href="https://robinhoodchain.blockscout.com"
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex items-center gap-1 text-sm text-emerald-300 hover:underline"
          >
            Explorer <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </section>
      </main>
    </div>
  );
}
